import ChevronDownThin from '@assets/icons/chevron-down-thin.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Text, TouchableOpacity, View } from 'react-native';
import {
  CheckIn_CheckInItemIssuesFieldsFragment,
  OrderInvoiceCheckInItemStatus,
} from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import {
  GroupedIssueByStatus,
  OrderCheckInIssue,
} from './order-check-in-issue';

interface Props {
  checkInItemUUID: string;
  checkInUUID: string;
  issues?: CheckIn_CheckInItemIssuesFieldsFragment[];
}

export const OrderCheckInIssuesList: React.FC<Props> = ({
  checkInItemUUID,
  checkInUUID,
  issues,
}) => {
  const [issuesListOpen, setIssuesListOpen] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(0);
  const animatedValueRef = useRef(new Animated.Value(0));

  const groupedIssuesByStatus = useMemo((): GroupedIssueByStatus[] => {
    if (!issues) {
      return [];
    }

    const groupedRecord = issues.reduce(
      (acc, issue) => {
        const { status, quantity, uuid } = issue;

        if (!acc[status]) {
          acc[status] = {
            status: status as OrderInvoiceCheckInItemStatus,
            totalQuantity: 0,
            uuids: [],
          };
        }

        acc[status].uuids.push(uuid);
        acc[status].totalQuantity += quantity;

        return acc;
      },
      {} as Record<string, GroupedIssueByStatus>,
    );

    return Object.values(groupedRecord);
  }, [issues]);

  useEffect(() => {
    Animated.timing(animatedValueRef.current, {
      duration: 250,
      easing: Easing.out(Easing.exp),
      toValue: issuesListOpen ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [issuesListOpen]);

  const [styles, theme] = useStyles();
  return issues && groupedIssuesByStatus.length > 0 ? (
    <View style={styles.issuesWrapper}>
      {groupedIssuesByStatus.length === 1 ? (
        <OrderCheckInIssue
          checkInItemUUID={checkInItemUUID}
          checkInUUID={checkInUUID}
          groupedIssue={groupedIssuesByStatus[0]}
        />
      ) : (
        <View>
          <TouchableOpacity
            onPress={() => {
              setIssuesListOpen((open) => !open);
            }}
            style={styles.touchable}
          >
            <Text style={styles.title}>
              {i18n.t('check_in.view_issues', { count: issues.length })}
            </Text>
            <Animated.View
              style={[
                styles.chevronButton,
                {
                  transform: [
                    {
                      rotate: animatedValueRef.current.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '-180deg'],
                      }),
                    },
                  ],
                },
              ]}
            >
              <ChevronDownThin
                height={16}
                stroke={theme.sk.color.icon.tertiary}
                width={16}
              />
            </Animated.View>
          </TouchableOpacity>
          <Animated.View
            style={[
              styles.container,
              {
                height: animatedValueRef.current.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, currentHeight],
                }),
              },
            ]}
          >
            <View
              onLayout={(e) => {
                setCurrentHeight(e.nativeEvent.layout.height);
              }}
              style={styles.content}
            >
              {groupedIssuesByStatus.map((issue) => (
                <OrderCheckInIssue
                  checkInItemUUID={checkInItemUUID}
                  checkInUUID={checkInUUID}
                  groupedIssue={issue}
                  key={issue.status}
                />
              ))}
            </View>
          </Animated.View>
        </View>
      )}
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  chevronButton: {
    padding: 4,
  },
  container: {
    overflow: 'hidden',
  },
  content: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  issuesWrapper: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  title: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.tertiary,
    fontSize: theme.fontSizes.small,
    lineHeight: 16,
  },
  touchable: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 8,
    paddingHorizontal: 4,
  },
}));
